import { WithMsalProps, useIsAuthenticated } from '@azure/msal-react'
import { observer } from 'mobx-react'
import React from 'react'
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom'
import Admin from '../admin/Admin'
import LoginPage from '../auth/LoginPage'
import RestrictedRoute from '../auth/RestrictedRoute'
import TokenPage from '../auth/TokenPage'
import { User } from '../auth/types'
import withUser from '../auth/withUser'
import BetaPage from '../Beta/BetaPage'
import betaStore, { BetaFeature } from '../Beta/betaStore'
import BudgetPlanning from '../budgetPlanning/BudgetPlanning'
import CampaignPage from '../campaign/CampaignPage'
import CustomerTargetRoot from '../customerTarget/CustomerTargetRoot'
import DailyInfo from '../dailyInfo/DailyInfo'
import DailyManagementRoot from '../dailyManagement/DailyManagementRoot'
import Dashboard from '../Dashboard/Dashboard'
import EmployeeCard from '../employeeCard/EmployeeCard'
import ExpressCheckoutPage from '../expressCheckout/ExpressCheckoutPage'
import GuidelinesAndLinks from '../guidelinesAndLinks/GuidelinesAndLinks'
import IntroductionLinks from '../introductionLinks/IntroductionLinks'
import MonthlyInfoRoot from '../monthlyInfo/MonthlyInfoRoot'
import NotFound from '../notFound/NotFound'
import OldDashboard from '../OldDashboard/OldDashboard'
import Products from '../products/Products'
import Reporting from '../reporting/Reporting'
import ScoreCardPage from '../scoreCard/ScoreCardPage'
import NoStationPage from '../station/NoStationPage'
import StationPage from '../station/StationPage'
import withActiveStation, { WithActiveStationProps } from '../station/withActiveStation'
import TaskList from '../taskList/TaskList'
import TaskListCreatePage from '../taskList/TaskListCreatePage'
import NoGroupPage from '../user/NoGroupPage'
import WeeklyInfoRoot from '../weeklyInfo/WeeklyInfoRoot'
import Reminders from './Reminders'

type InjectedProps = {
  user: User | null | undefined
}

type Props = RouteComponentProps & WithMsalProps & InjectedProps & WithActiveStationProps

// Picks new or old Etusivu based on beta opt-in. Forwards all props from <Route>.
const EtusivuRoute = observer((props: RouteComponentProps) => {
  const isNewEtusivu = betaStore.isEnabled(BetaFeature.NewEtusivu)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return isNewEtusivu ? <Dashboard {...props} /> : <OldDashboard {...(props as any)} />
})

const Routes = observer(({ location, activeStation, user }: Props) => {
  const isAuthenticated = useIsAuthenticated()

  const shouldRedirectToLogin = !isAuthenticated && location.pathname !== '/kirjaudu'

  if (shouldRedirectToLogin) {
    return <Redirect to='/kirjaudu' />
  }

  if (user && 'Unknown' === user.userType && location.pathname !== '/ei-ryhmää') {
    return <Redirect to='/ei-ryhmää' />
  }

  const paths = ['/valitse-toimipaikka', '/kirjaudu', '/ei-toimipaikkaa', '/ei-ryhmää']

  if (!activeStation && !paths.includes(location.pathname)) {
    return <Redirect to='/valitse-toimipaikka' />
  }

  return (
    <Reminders>
      <Switch>
        <Route exact path='/' component={EtusivuRoute} />
        <Route exact path='/:date(\d{4}-\d{2}-\d{2})' component={EtusivuRoute} />
        <Route exact path='/beta' component={BetaPage} />
        <RestrictedRoute
          requiredPermission='nav_basic'
          path='/asiakasmaaratavoitteet-seka-toteumat'
          component={CustomerTargetRoot}
        />
        <RestrictedRoute requiredPermission='nav_basic' path='/vitriinien-esillepano' component={Products} />
        <RestrictedRoute requiredPermission='nav_basic' path='/vuorokausijohtaminen' component={DailyManagementRoot} />
        <RestrictedRoute
          requiredPermission='nav_basic'
          exact
          path='/tehtavalista/uusi-tehtava'
          component={TaskListCreatePage}
        />
        <RestrictedRoute requiredPermission='nav_basic' path='/tehtavalista' component={TaskList} />
        <RestrictedRoute requiredPermission='nav_basic' path='/tuloskortti/:stationId' component={ScoreCardPage} />
        <RestrictedRoute requiredPermission='manage_chain' path='/tuloskortti' exact component={ScoreCardPage} />
        <RestrictedRoute requiredPermission='nav_basic' exact path='/myynnit-ja-tunnit' component={BudgetPlanning} />
        <RestrictedRoute requiredPermission='nav_basic' exact path='/ravintolainfo' component={DailyInfo} />
        <RestrictedRoute requiredPermission='nav_basic' exact path='/ravintolainfo/:date' component={DailyInfo} />
        <RestrictedRoute
          requiredPermission='nav_basic'
          exact
          path='/ravintolainfo/:date/muokkaa'
          component={(props) => <DailyInfo {...props} edit />}
        />
        <RestrictedRoute requiredPermission='nav_basic' exact path='/tyontekijakortti' component={EmployeeCard} />
        <RestrictedRoute requiredPermission='nav_basic' path='/viikkoinfo' component={WeeklyInfoRoot} />
        <RestrictedRoute requiredPermission='nav_basic' path='/kuukausi-info' component={MonthlyInfoRoot} />
        <RestrictedRoute requiredPermission='nav_basic' path='/tyontekijakortti' component={EmployeeCard} />
        <RestrictedRoute requiredPermission='nav_basic' path='/raportointi' component={Reporting} />
        <RestrictedRoute requiredPermission='nav_basic' path='/yllapito' component={Admin} />
        <RestrictedRoute requiredPermission='nav_basic' path='/ohjeet-ja-linkit' component={GuidelinesAndLinks} />
        <RestrictedRoute requiredPermission='nav_basic' path='/perehdytys' component={IntroductionLinks} />
        <RestrictedRoute requiredPermission='nav_basic' path='/kampanjat' component={CampaignPage} />
        <RestrictedRoute requiredPermission='nav_basic' path='/pikakassa/:stationId' component={ExpressCheckoutPage} />
        <Route exact path='/kirjaudu' component={LoginPage} />
        <Route exact path='/valitse-toimipaikka' component={StationPage} />
        <Route exact path='/ei-toimipaikkaa' component={NoStationPage} />
        <Route exact path='/ei-ryhmää' component={NoGroupPage} />
        <Route exact path='/token' component={TokenPage} />
        <Route component={NotFound} />
      </Switch>
    </Reminders>
  )
})

export default withUser(withRouter(withActiveStation(Routes)))
